import React, { useEffect, useState } from 'react';
import SelectReact, { ActionMeta, GroupBase, OnChangeValue, Props, PropsValue } from 'react-select';
import styled from 'styled-components';
import { ExtendedReactSelectProps, ISelectStyleProps, SelectRef, SelectType } from './selectTypes';
import { selectStyles } from './selectStyles';
import { useTestId } from '../../hooks/useTestId';
import { SelectErrorMessageContainer } from './SelectComponents';

export const SelectContainer = styled.div<ISelectStyleProps>`
  ${selectStyles};
`;

export const Select = React.forwardRef(function Select<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: Props<Option, IsMulti, Group> & ExtendedReactSelectProps,
  ref: SelectRef<Option, IsMulti, Group>
) {
  const { generateTestIdClassName, generateTestId } = useTestId(props, 'Select');
  const { invalid, animateDropdownIcon, bare, requiredErrorMessage, required, defaultValue, textAlign } = props;
  const [isInvalid, setIsInvalid] = useState(invalid);
  const [currentValue, setCurrentValue] = useState<PropsValue<Option> | undefined>(defaultValue);

  const onChange = (option: OnChangeValue<Option, IsMulti>, actionMeta: ActionMeta<Option>) => {
    setCurrentValue(option);

    const currentValueInvalid = !option || (Array.isArray(option) && option.length === 0);
    if (required) {
      setIsInvalid(currentValueInvalid);
    }

    if (props.onChange) {
      props.onChange(option, actionMeta);
    }
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const currentValueInvalid = !currentValue || (Array.isArray(currentValue) && currentValue.length === 0);
    if (required) {
      setIsInvalid(currentValueInvalid);
    }

    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  useEffect(() => {
    setIsInvalid(invalid);
  }, [invalid]);

  return (
    <SelectContainer invalid={isInvalid} animateDropdownIcon={animateDropdownIcon} bare={bare} textAlign={textAlign} {...generateTestId()}>
      <SelectReact
        ref={ref}
        {...props}
        classNamePrefix='react-select'
        {...generateTestIdClassName()}
        onChange={onChange}
        onBlur={onBlur}
        aria-invalid={isInvalid}
      />
      <SelectErrorMessageContainer invalid={isInvalid} requiredErrorMessage={requiredErrorMessage} />
    </SelectContainer>
  );
}) as SelectType<ExtendedReactSelectProps>;
